// Acquisition attribution: the entry link's UTM tags, captured once per tab
// and stamped on the search when Ava mints it.
//
// The row is minted on the patient's FIRST ANSWER, an arbitrary time after
// landing, and by then the URL may have been rewritten (the pane-of-glass
// navigates to /ava?search=… on a rail click; Gatsby's client routing drops
// the query on internal links). So the tags are read off the URL the moment a
// page loads, kept in sessionStorage for the life of the TAB, and scrubbed
// from the address bar — a tag left in the URL rides into copied links, into
// history, and into the cross-origin referrer the map iframe sends.
//
// Captured on EVERY page (gatsby-browser onRouteUpdate), not just /ava and
// /book: a paid click lands on a landing page and reaches Ava one click
// later, and that click must not need to forward the tags itself. A later
// tagged URL in the same tab overwrites (last link wins); an untagged URL
// leaves the stash alone. A returning patient days later, in a new tab, starts
// an untagged search — correct, the tags describe the acquisition of a search,
// not of an account.
//
// What is captured: utm_source, utm_medium, utm_campaign, utm_content. What is
// NOT, on purpose: utm_term (the matched keyword would record a condition
// before the patient tapped anything) and gclid (a click id that could tie a
// booking back to a Google click). Both are scrubbed from the URL with the
// rest so they do not linger either. The server validates the values (opaque
// lowercase codes, <= 64 chars) and drops an invalid one rather than failing
// the start; the client only trims and caps. See practicemd-api
// docs/Analytics-and-Observability.md, "Acquisition attribution".
//
// Dependency-free on purpose, like accountFlowParams: gatsby-browser imports
// it at app boot, and a test imports it without a DOM.

export interface UTM {
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmContent?: string
}

export const ATTRIBUTION_STORAGE_KEY = 'ava_utm'

// Client-side cap only; the server's own limit (64) is the real rule. This
// just keeps a hostile URL from stuffing sessionStorage.
const MAX_CLIENT_LEN = 256

// Every parameter the capture consumes and therefore scrubs from the URL,
// including the two it deliberately never stores.
const SCRUBBED_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'src']

// The legacy email-wave tag. Five waves went out as /ava?src=ava-intro-wN
// before UTM existed; those links live in inboxes indefinitely. Only that one
// shape is honored — any other src value is scrubbed and ignored.
const LEGACY_SRC = /^ava-intro-[a-z0-9-]+$/

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

function clean(v: string | null): string | undefined {
  if (v === null) return undefined
  const t = v.trim().slice(0, MAX_CLIENT_LEN)
  return t === '' ? undefined : t
}

/** Parses the UTM set out of a query string. null when the URL carries no tag. */
export function parseUTM(search: string | null | undefined): UTM | null {
  if (!search) return null
  const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const utm: UTM = {}
  const src = clean(q.get('utm_source'))
  const med = clean(q.get('utm_medium'))
  const camp = clean(q.get('utm_campaign'))
  const cont = clean(q.get('utm_content'))
  if (src) utm.utmSource = src
  if (med) utm.utmMedium = med
  if (camp) utm.utmCampaign = camp
  if (cont) utm.utmContent = cont
  if (Object.keys(utm).length > 0) return utm

  const legacy = clean(q.get('src'))
  if (legacy && LEGACY_SRC.test(legacy)) {
    return { utmSource: 'practicemd', utmMedium: 'email', utmCampaign: legacy }
  }
  return null
}

/** Stores the set for the tab. Storage failures (private mode, quota) are swallowed. */
export function storeUTM(utm: UTM, storage: StorageLike | null): void {
  if (!storage) return
  try {
    storage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(utm))
  } catch {
    /* no storage: the tag is lost for this tab, the search still starts */
  }
}

/** Empties the tab's stash. Storage failures are swallowed. */
export function clearUTM(storage: StorageLike | null): void {
  if (!storage) return
  try {
    storage.removeItem(ATTRIBUTION_STORAGE_KEY)
  } catch {
    /* no storage: nothing was stored either */
  }
}

/** The tab's stored set, or null. */
export function readUTM(storage: StorageLike | null): UTM | null {
  if (!storage) return null
  try {
    const raw = storage.getItem(ATTRIBUTION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    const out: UTM = {}
    for (const k of ['utmSource', 'utmMedium', 'utmCampaign', 'utmContent'] as const) {
      const v = parsed[k]
      if (typeof v === 'string' && v !== '') out[k] = v.slice(0, MAX_CLIENT_LEN)
    }
    return Object.keys(out).length > 0 ? out : null
  } catch {
    return null
  }
}

/**
 * Removes the attribution parameters from a URL's query, keeping every other
 * parameter (?search=, ?request=, …) in place. Returns null when nothing
 * needed removing, so a caller can skip the history write.
 */
export function scrubAttribution(url: string): string | null {
  const qIdx = url.indexOf('?')
  if (qIdx < 0) return null
  const hashIdx = url.indexOf('#', qIdx)
  const query = hashIdx >= 0 ? url.slice(qIdx + 1, hashIdx) : url.slice(qIdx + 1)
  const hash = hashIdx >= 0 ? url.slice(hashIdx) : ''
  const q = new URLSearchParams(query)
  let touched = false
  for (const p of SCRUBBED_PARAMS) {
    if (q.has(p)) {
      q.delete(p)
      touched = true
    }
  }
  if (!touched) return null
  const rest = q.toString()
  return url.slice(0, qIdx) + (rest ? '?' + rest : '') + hash
}

/**
 * The per-page capture: read the tags off the current URL, keep them for the
 * tab, and clean the address bar. Wired from gatsby-browser onRouteUpdate.
 * SSR-safe (no window → no-op).
 */
export function captureAttribution(location?: { search?: string } | null): void {
  if (typeof window === 'undefined') return
  const search = location?.search ?? window.location.search
  let storage: StorageLike | null = null
  try {
    storage = window.sessionStorage
  } catch {
    storage = null
  }
  const utm = parseUTM(search)
  if (utm) storeUTM(utm, storage)
  const href = window.location.pathname + window.location.search + window.location.hash
  const scrubbed = scrubAttribution(href)
  if (scrubbed !== null) {
    try {
      window.history.replaceState(window.history.state, '', scrubbed)
    } catch {
      /* a history API that refuses is not worth failing the page over */
    }
  }
}

/** What Ava hands to startAvaSearch: the tab's stash, if any. */
export function currentAttribution(): UTM | undefined {
  if (typeof window === 'undefined') return undefined
  let storage: StorageLike | null = null
  try {
    storage = window.sessionStorage
  } catch {
    return undefined
  }
  return readUTM(storage) ?? undefined
}

/**
 * Clears the tab's stash: Find Another Provider (practicemd-api
 * docs/Referrals-Architecture.md §10.3), so the rated clinician's tags never
 * follow the patient into the search they start next. A cleared stash stays
 * cleared, because the capture stores only from a tagged URL. "+ New search"
 * does not clear: a new search after an ad click is still the ad's.
 */
export function clearAttribution(): void {
  if (typeof window === 'undefined') return
  let storage: StorageLike | null = null
  try {
    storage = window.sessionStorage
  } catch {
    return
  }
  clearUTM(storage)
}
